import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { DataService } from "./data.service";
import { InvoiceLogError, InvoiceLogGetPaginated, InvoiceLogPost, PostInvoiceAttemptSummary, PostInvoiceAuditEvent } from "@app/interface/log-invoices-interfaces";
import { normalizeAuditError } from "@app/shared/util/audit-sanitizer.util";
import { environment } from "@env/environment";
import { catchError, first, firstValueFrom, Observable, of, switchMap } from "rxjs";
import { ApiPaginatedInvoiceLogsQueryParams, ApiPaginatedResponse, PaginationService } from "./pagination.service";
import { API_ERRORS } from "@app/shared/lists/errors";

@Injectable
({
  providedIn: 'root'
})
export class LogInvoicesService {
  private _http = inject(HttpClient);
  private _dataService = inject(DataService);
  private _baseUrl = `${environment.mkgoURL}/api/v1/log-invoices`

  async create(invoiceLog: InvoiceLogPost | PostInvoiceAuditEvent) {
    const options = await firstValueFrom(this._dataService.httpOptions(this._dataService.company.cnpj));
    return await firstValueFrom(this._http.post(this._baseUrl, invoiceLog, options));
  }

  /** Shared, sanitizing normalizer (Error, HttpErrorResponse, plain objects); never fabricates a stack. */
  normalizeError(error: unknown, context?: Record<string, unknown>): InvoiceLogError {
    return normalizeAuditError(error, context);
  }

  /** Derived per-attempt summaries of the post-invoice timeline of one OS (status computed server-side). */
  getAttempts(osId: string): Observable<{ attempts: PostInvoiceAttemptSummary[] }> {
    const params = PaginationService.getParams({ osId });
    return this._dataService.httpOptions(this._dataService.company.cnpj, params).pipe(
      switchMap(options => this._http.get<{ attempts: PostInvoiceAttemptSummary[] }>(`${this._baseUrl}/attempts`, options)),
      first(),
    );
  }

  getPaginated(apiParams: ApiPaginatedInvoiceLogsQueryParams): Observable<ApiPaginatedResponse<InvoiceLogGetPaginated>> {
    const url = `${this._baseUrl}/pages`;
    let params = PaginationService.getParams(apiParams);
    return this._dataService.httpOptions(this._dataService.company.cnpj, params).pipe(
      switchMap(options => this._http.get<ApiPaginatedResponse<InvoiceLogGetPaginated>>(url, options)),
      first(),
      catchError(err => {
        if (err.error.error === API_ERRORS.notFound) {
          return of({
            docs: [],
            hasNextPage: false,
            hasPrevPage: false,
            limit: 0,
            nextPage: null,
            page: 0,
            pagingCounter: 0,
            prevPage: null,
            totalDocs: 0,
            totalPages: 0
          })
        } else {
          throw err;
        }
      }),
    );
  }
}